import React, { Component } from 'react';
import classnames from 'classnames';
import {
  DEFAULT_IB_AUTH_COOKIE_NAME,
  removeAuthCookie,
} from 'web-lib-application-essentials/lib/cookie';
import { getAuthDomain } from 'web-lib-application-essentials/lib/urlUtil';
import { NavigationButton } from 'web-ui-components/lib/navigation/buttons';
import InternetBankingActions from '../../actions/InternetBankingActions';
import {
  getMTrackConsented,
  handleMTrackConsentedError,
} from '../../actions/MTrackActionCreator';
import {
  M_TRACK_CONSENTED_CTA,
  M_TRACK_CONSENTED_TEXT,
  M_TRACK_LOGO,
  M_TRACK_SIGNUP_CTA,
  M_TRACK_SIGNUP_TEXT,
} from '../../constants/MTrackConstants';
import MTrackStore from '../../stores/MTrackStore';
import SessionStore from '../../stores/SessionStore';
import {
  getShortTermToken,
  getThirdPartyRedirect,
} from '../../utils/ThirdPartyApiUtils';
import LoaderComponent from '../common/LoaderComponent';

class MTrackTile extends Component {
  constructor(props) {
    super(props);

    this.onMTrackStoreChange = this.onMTrackStoreChange.bind(this);
    this.getContent = this.getContent.bind(this);
    this.handleCtaClick = this.handleCtaClick.bind(this);

    this.state = {
      loading: false,
      hasMTrackConsentLoaded: MTrackStore.getMTrackConsentLoaded(),
      userHasConsented: MTrackStore.isMTrackConsented(),
      consentApiError: false,
      redirectURL: null,
    };
  }

  componentDidMount() {
    MTrackStore.addChangeListener(this.onMTrackStoreChange);
    getMTrackConsented();
  }

  componentWillUnmount() {
    MTrackStore.removeChangeListener(this.onMTrackStoreChange);
  }

  onMTrackStoreChange() {
    const mTrackConsentLoaded = MTrackStore.getMTrackConsentLoaded();
    const mTrackConsented = MTrackStore.isMTrackConsented();
    const mTrackConsentError = MTrackStore.isMTrackConsentError();
    const mTrackConsentErrorMessage = MTrackStore.getMTrackConsentError();

    this.setState({
      ...(mTrackConsentLoaded !== this.state.hasMTrackConsentLoaded && {
        hasMTrackConsentLoaded: mTrackConsentLoaded,
      }),
      ...(mTrackConsented && { userHasConsented: mTrackConsented }),
      ...(mTrackConsentError &&
        mTrackConsentErrorMessage && {
          consentApiError: mTrackConsentError,
        }),
      ...(mTrackConsented &&
        mTrackConsentError && { consentApiError: mTrackConsentError }),
    });
  }

  getContent() {
    const { consentApiError, userHasConsented } = this.state;
    if (consentApiError) {
      return {
        logo: M_TRACK_LOGO,
        text: (
          <div>
            We’re busy updating your dashboard to help you track and plan.
            <br />
            <br />
            It won’t be long before you can get the big picture on your
            business.
          </div>
        ),
        image: '../images/vm/m-track/laptop-mtrack.png',
      };
    } else {
      return {
        logo: M_TRACK_LOGO,
        text: userHasConsented ? M_TRACK_CONSENTED_TEXT : M_TRACK_SIGNUP_TEXT,
        ctaText: userHasConsented ? M_TRACK_CONSENTED_CTA : M_TRACK_SIGNUP_CTA,
        image: `../images/vm/m-track/laptop-mtrack.png`,
      };
    }
  }

  handleCtaClick() {
    this.setState({
      loading: true,
    });

    if (this.state.userHasConsented) {
      this.attemptRedirect();
    } else {
      InternetBankingActions.navigateToWebTask('WEB-M-TRACK');
    }
  }

  attemptRedirect = async () => {
    try {
      SessionStore.setTokenDelete(false);

      const redirectUrl = await getThirdPartyRedirect({});
      const token = await getShortTermToken();
      const redirectUrlWithToken = redirectUrl + '?id_token=' + token;
      const form = document.getElementById('mtrack-form');
      form.action = redirectUrl;
      document.getElementById('id_token').value = token;

      if (redirectUrlWithToken !== null) {
        /* history.pushState temp fix in place to allow back button navigation from 9S */
        const urlPath = window.location.href?.includes('?')
          ? window.location.href + '&customer_type=business'
          : window.location.href + '/?customer_type=business';
        window.history.pushState(null, '', urlPath);

        /* Fix for BUS-15010 to allow logout from 9S to return to login page */
        if (process.env.NODE_ENV !== 'development') {
          removeAuthCookie(DEFAULT_IB_AUTH_COOKIE_NAME, getAuthDomain());
        }
        form.submit();
      } else {
        handleMTrackConsentedError();
      }
    } catch (e) {
      handleMTrackConsentedError();
      this.setState({
        loading: false,
        consentApiError: true,
      });
    }
  };

  render() {
    const { image, logo, text, ctaText } = this.getContent();

    const mTrackTile = this.state.loading ? (
      <LoaderComponent type="large" />
    ) : (
      <div
        className={classnames('mtrack__tile c-bfeatures l-tile', {
          'track__tile--error': this.state.consentApiError,
        })}
        data-test-id="mtrack-tile"
      >
        <div className="c-bfeatures__body mtrack-tile__content">
          <LoaderComponent
            type="small"
            className={`mtrack-tile__loader-vm${
              this.state.hasMTrackConsentLoaded ? '--hidden' : ''
            }`}
          />
          <div
            style={{
              display: this.state.hasMTrackConsentLoaded ? '' : 'none',
            }}
          >
            <div className="mtrack-tile__header">
              <div className="mtrack-tile__logo">
                <img src={logo} alt="M-Track" />
              </div>
              <div className="mtrack-tile__image">
                <img src={image} alt="" />
              </div>
            </div>
            <div className="mtrack-tile__body">
              <div className="mtrack-tile__description">{text}</div>
              <p className="c-bfeatures__cta mtrack-tile__cta-btn-wrap">
                {!this.state.consentApiError && (
                  <NavigationButton.Primary
                    labelText={ctaText}
                    onClick={this.handleCtaClick}
                    data-test-id="mtrack-tile-button"
                  />
                )}
              </p>
            </div>
          </div>
        </div>
      </div>
    );

    return (
      <form
        className="mtrack-tile__form"
        id="mtrack-form"
        action=""
        method="POST"
      >
        <input
          className="hidden"
          autoComplete="off"
          id="id_token"
          name="id_token"
          type="hidden"
        />
        {mTrackTile}
      </form>
    );
  }
}

export { MTrackTile };
